// src/pages/DebtStudio/components/Issues/DealStructure/SolutionParameters.jsx
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "lib/dayjs";
import { useDebtStudio } from "pages/DebtStudio/context/DebtStudioContext";
import { supabase } from "services/supabaseClient";
import { useDialog } from "context/DialogContext";
import { DropdownSelect, TextInput } from "components/InputStyled";
import { useLinkedFund } from "./hooks/useLinkedFund";
import { REFUNDING_SOLUTION_OPTIONS, enabledRefundingSolutionType } from "pages/DebtStudio/lib/refundingSolutionTypes";
import WrapCashflowModal from "./WrapCashflowModal";
import { getCombinationRequirements, isCombinationPurpose } from "pages/DebtStudio/lib/combination";

function formatNumber(val) {
  if (val == null || isNaN(val)) return "";
  return Number(val).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatInteger(val) {
  const n = Number(val);
  if (!Number.isFinite(n)) return "";
  return String(Math.round(n));
}

function parseNumber(str) {
  if (!str) return null;
  const cleaned = String(str).replace(/,/g, "");
  const n = parseFloat(cleaned);
  return isNaN(n) ? null : n;
}

function parseInteger(str) {
  if (!str) return null;
  const cleaned = String(str).replace(/,/g, "");
  const n = parseInt(cleaned, 10);
  return Number.isFinite(n) ? n : null;
}

function num(x) {
  const n = Number(x);
  return Number.isFinite(n) ? n : null;
}

function isBlankish(v) {
  if (v == null) return true;
  if (typeof v === "string") return v.trim() === "";
  return false;
}

function normalizeFundingType(v) {
  return v === "net" ? "net" : "gross";
}

function safeJson(value, fallback) {
  if (value == null) return fallback;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch {
      return fallback;
    }
  }
  return value;
}

function nearlyEqual(a, b, tol = 0.01) {
  const A = Number(a);
  const B = Number(b);
  if (!Number.isFinite(A) || !Number.isFinite(B)) return false;
  return Math.abs(A - B) <= tol;
}

function normalizeFinancingType(v) {
  return String(v || "").trim().toLowerCase();
}

function normalizeFrequency(v) {
  return String(v || "").trim().toLowerCase();
}

function frequencyToMonths(freq) {
  const f = normalizeFrequency(freq);

  if (f.startsWith("month")) return 1;
  if (f.startsWith("bi-month") || f === "bimonthly") return 2;
  if (f.startsWith("quarter")) return 3;
  if (f.startsWith("every_four") || f.startsWith("every four")) return 4;
  if (f.startsWith("triannual")) return 4;
  if (f.startsWith("semi")) return 6;
  if (f.startsWith("annual")) return 12;

  return 12;
}

function deriveLoanTermPeriods(form, structureRows) {
  const start =
    form?.firstPrincipalDate ||
    form?.first_principal_date ||
    form?.firstPaymentDate ||
    form?.first_payment_date ||
    null;

  const end =
    form?.finalMaturityDate ||
    form?.final_maturity_date ||
    form?.maturityDate ||
    form?.maturity_date ||
    null;

  const freq =
    form?.principalFrequency ||
    form?.principal_frequency ||
    form?.paymentFrequency ||
    form?.payment_frequency ||
    form?.couponFrequency ||
    form?.coupon_frequency ||
    null;

  const months = frequencyToMonths(freq);

  if (start && end && months > 0) {
    const s = dayjs(start);
    const e = dayjs(end);

    if (s.isValid() && e.isValid() && !e.isBefore(s, "day")) {
      let count = 0;
      let d = s;

      while (!d.isAfter(e, "day") && count < 1000) {
        count += 1;
        d = d.add(months, "month");
      }

      return count > 0 ? count : null;
    }
  }

  if (Array.isArray(structureRows)) {
    const count = structureRows.filter((r) => {
      const p =
        num(r?.principal) ??
        num(r?.par) ??
        num(r?.parAmount) ??
        num(r?.par_amount) ??
        num(r?.amount);

      return p != null && p > 0;
    }).length;

    return count > 0 ? count : null;
  }

  return null;
}

function normalizeWrapConfig(value) {
  const raw = safeJson(value, {});
  const wrapPaymentRows = Array.isArray(raw?.wrapPaymentRows)
    ? raw.wrapPaymentRows
        .map((r) => ({
          date: r?.date || "",
          total: Number.isFinite(Number(r?.total)) ? Number(r.total) : 0,
        }))
        .filter((r) => r.date || r.total)
    : [];

  return {
    enabled: !!raw?.enabled,
    sourceMode: raw?.sourceMode || "manual",
    inputMode: raw?.inputMode || "dated_totals",
    wrapPaymentRows,
  };
}

function normalizeWrapManualCashflows(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((r) => ({
      date: r?.date || "",
      total: Number.isFinite(Number(r?.total)) ? Number(r.total) : 0,
    }))
    .filter((r) => r.date || r.total);
}

function computeWrapCashflowStats(wrapManualCashflows, wrapConfig) {
  const directRows = normalizeWrapManualCashflows(wrapManualCashflows);
  const cfg = normalizeWrapConfig(wrapConfig);
  const rows = directRows.length > 0 ? directRows : cfg.wrapPaymentRows;

  const validRows = rows.filter(
    (r) => r?.date && Number.isFinite(Number(r?.total))
  );
  const totalAmount = validRows.reduce(
    (sum, r) => sum + Number(r.total || 0),
    0
  );

  return {
    count: validRows.length,
    totalAmount,
  };
}

function computeRefundingDepositsTotal(refundedIssues) {
  const arr = safeJson(refundedIssues, []);
  if (!Array.isArray(arr) || arr.length === 0) return null;

  const pickDeposit = (r) => {
    if (!r || typeof r !== "object") return null;

    const candidates = [
      r.deposit_required,
      r.depositRequired,
      r.deposit_requirement,
      r.depositRequirement,
      r.deposit_amount,
      r.depositAmount,
      r.deposit,
      r.amount,
      r.value,
    ];

    for (const v of candidates) {
      const n = num(v);
      if (n != null) return n;
    }
    return null;
  };

  const total = arr.reduce((sum, r) => sum + (pickDeposit(r) || 0), 0);
  return Number.isFinite(total) && total > 0 ? total : null;
}

function DisplayField({ children, title, ariaLabel, className = "" }) {
  return (
    <div
      aria-label={ariaLabel}
      title={title}
      className={`h-8 px-3 rounded-md border border-input bg-background text-foreground shadow-sm flex items-center text-sm min-w-[220px] ${className}`}
    >
      {children}
    </div>
  );
}

export default function SolutionParameters({
  isLocked = false,
  onSolvePrincipal = null,
  canSolvePrincipal = false,
}) {
  const navigate = useNavigate();
  const { showDialog } = useDialog();

  const {
    form,
    setForm,
    structureRows,
    selectedOrg,
    ensureStructure,
    saveDeal,
  } = useDebtStudio();

  const { structureMode, targetType, targetValue, parAmount } = form;

  const [wrapModalOpen, setWrapModalOpen] = useState(false);

  const isLoan = normalizeFinancingType(form?.financingType) === "loan";

  const loanTermPeriods = useMemo(
    () => deriveLoanTermPeriods(form, structureRows),
    [form, structureRows]
  );

  const [amortizationPeriodsInput, setAmortizationPeriodsInput] = useState(
    formatInteger(form?.amortizationPeriods ?? loanTermPeriods)
  );

  const wrapConfig = useMemo(
    () => normalizeWrapConfig(form?.wrapConfig),
    [form?.wrapConfig]
  );

  const wrapStats = useMemo(
    () => computeWrapCashflowStats(form?.wrapManualCashflows, form?.wrapConfig),
    [form?.wrapManualCashflows, form?.wrapConfig]
  );

  const isRefundingDeal =
    String(form?.deal_type || form?.dealType || "").toLowerCase() ===
    "refunding";
  const isCombinationDeal = isCombinationPurpose(form?.deal_type || form?.dealType);
  const combinationRequirements = getCombinationRequirements(form);

  const lastLoanTermPeriodsRef = useRef(null);

  const linkedFundType = isRefundingDeal ? "refunding" : "project";

  const fundingType = normalizeFundingType(form?.fundingType || "gross");
  const targetGross = num(form?.targetGross);
  const investedNetProceeds = form?.investedNetProceeds ?? null;

  const [inputValue, setInputValue] = useState(formatNumber(targetValue));
  const [grossInputValue, setGrossInputValue] = useState(
    formatNumber(targetGross)
  );

  const userEdited = useRef(false);
  const seededParFromDealInfo = useRef(false);

  const hasSolvedStructure = useMemo(() => {
    if (!Array.isArray(structureRows) || structureRows.length === 0) {
      return false;
    }

    const principalKeys = [
      "principal",
      "par",
      "parAmount",
      "par_amount",
      "amount",
    ];

    return structureRows.some((r) => {
      if (!r || typeof r !== "object") return false;
      for (const k of principalKeys) {
        const v = num(r[k]);
        if (v != null && v > 0) return true;
      }
      return false;
    });
  }, [structureRows]);

  const isNetProceeds = targetType === "net_proceeds";
  const isWrapMode = structureMode === "wrap_lds";

  const refundingDepositsTotal = useMemo(() => {
    if (!isRefundingDeal) return null;
    return computeRefundingDepositsTotal(form?.refunded_issues);
  }, [isRefundingDeal, form?.refunded_issues]);

  useEffect(() => {
    if (!isLoan) return;
    if (!(loanTermPeriods > 0)) return;

    const priorDerivedTerm = lastLoanTermPeriodsRef.current;
    const current = parseInteger(form?.amortizationPeriods);

    const termChanged =
      priorDerivedTerm != null && priorDerivedTerm !== loanTermPeriods;

    const shouldInitialize = current == null;
    const shouldResetForNewStructure = termChanged;

    if (!shouldInitialize && !shouldResetForNewStructure) {
      lastLoanTermPeriodsRef.current = loanTermPeriods;
      return;
    }

    lastLoanTermPeriodsRef.current = loanTermPeriods;

    setForm((f) => ({
      ...f,
      loanTermPeriods,
      amortizationPeriods: loanTermPeriods,
    }));
  }, [isLoan, loanTermPeriods, form?.amortizationPeriods, setForm]);

  useEffect(() => {
    if (!isLoan) return;

    const next = form?.amortizationPeriods ?? loanTermPeriods;
    setAmortizationPeriodsInput(formatInteger(next));
  }, [isLoan, form?.amortizationPeriods, loanTermPeriods]);

  useEffect(() => {
    if (!isRefundingDeal) return;

    if (form?.targetType !== "net_proceeds") {
      setForm((f) => ({ ...f, targetType: "net_proceeds" }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefundingDeal]);

  useEffect(() => {
    if (!isRefundingDeal) return;
    if (targetType !== "net_proceeds") return;

    const derived = num(refundingDepositsTotal);
    const current = num(form?.targetGross);

    const same =
      derived == null && current == null
        ? true
        : derived != null && current != null && nearlyEqual(derived, current);

    if (same) return;

    setForm((f) => ({
      ...f,
      targetGross: derived,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefundingDeal, targetType, refundingDepositsTotal]);

  useEffect(() => {
    if (targetType === "net_proceeds") return;
    setInputValue(formatNumber(targetValue));
  }, [targetValue, targetType]);

  useEffect(() => {
    if (targetType !== "net_proceeds") return;
    setGrossInputValue(formatNumber(targetGross));
  }, [targetGross, targetType]);

  useEffect(() => {
    if (targetType === "periodic_ds") return;
    if (targetType !== "par_amount") return;

    const p = num(parAmount);
    const tv = num(targetValue);

    const targetIsBlank = tv == null || tv === 0 || isBlankish(targetValue);
    const parIsValid = p != null && p > 0;

    if (
      !seededParFromDealInfo.current &&
      !userEdited.current &&
      !hasSolvedStructure &&
      parIsValid &&
      targetIsBlank
    ) {
      seededParFromDealInfo.current = true;
      setForm((f) => ({
        ...f,
        targetValue: p,
        targetValueCache: {
          ...(f?.targetValueCache || {}),
          par_amount: p,
        },
      }));
      setInputValue(formatNumber(p));
    }
  }, [targetType, parAmount, targetValue, hasSolvedStructure, setForm]);

  const orgId = selectedOrg?.id ?? null;
  const dealId = form?.id ?? null;

  const {
    loading: linkedFundLoading,
    fundId: linkedFundId,
    fundTotalAmount: linkedFundTotalAmount,
    totalPortfolioCost,
  } = useLinkedFund({ orgId, dealId, fundType: linkedFundType });

  const grossMismatch = useMemo(() => {
    const dsGross = num(form?.targetGross);
    const fpGross = num(linkedFundTotalAmount);
    return dsGross != null && fpGross != null && !nearlyEqual(dsGross, fpGross);
  }, [form?.targetGross, linkedFundTotalAmount]);

  useEffect(() => {
    if (orgId && dealId) return;
    if (investedNetProceeds == null) return;
    setForm((f) => ({ ...f, investedNetProceeds: null }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, dealId]);

  useEffect(() => {
    if (!orgId || !dealId) return;

    if (linkedFundLoading) return;

    if (!linkedFundId) {
      setForm((f) => {
        const prev = num(f?.investedNetProceeds);
        if (prev == null) return f;
        return { ...f, investedNetProceeds: null };
      });
      return;
    }

    if (grossMismatch) {
      setForm((f) => {
        const prev = num(f?.investedNetProceeds);
        if (prev == null) return f;
        return { ...f, investedNetProceeds: null };
      });
      return;
    }

    const cost = num(totalPortfolioCost);
    const nextVal = cost != null && cost > 0 ? cost : null;

    setForm((f) => {
      const prev = num(f?.investedNetProceeds);
      if ((prev ?? null) === (nextVal ?? null)) return f;
      return { ...f, investedNetProceeds: nextVal };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    orgId,
    dealId,
    linkedFundLoading,
    linkedFundId,
    grossMismatch,
    totalPortfolioCost,
  ]);

  useEffect(() => {
    if (targetType !== "net_proceeds") return;

    setForm((f) => {
      const mode = normalizeFundingType(f?.fundingType || "gross");
      const g = num(f?.targetGross);
      const inv = num(f?.investedNetProceeds);

      const next = { ...f };

      next.targetType = "net_proceeds";
      next.fundingType = mode;

      if (mode === "gross") {
        next.targetValue = g != null && g > 0 ? g : null;
      } else {
        next.targetValue = inv != null && inv > 0 ? inv : null;
      }

      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetType, fundingType, investedNetProceeds, form?.targetGross]);

  const commitGenericTargetValue = () => {
    const parsed = parseNumber(inputValue);
    userEdited.current = true;

    if (parsed != null) {
      setForm((f) => ({
        ...f,
        targetValue: parsed,
        targetValueCache: {
          ...(f?.targetValueCache || {}),
          [f?.targetType || "par_amount"]: parsed,
        },
      }));
      setInputValue(formatNumber(parsed));
    } else {
      setInputValue("");
      setForm((f) => ({
        ...f,
        targetValue: null,
        targetValueCache: {
          ...(f?.targetValueCache || {}),
          [f?.targetType || "par_amount"]: null,
        },
      }));
    }
  };

  const commitTargetGross = () => {
    const parsed = parseNumber(grossInputValue);
    userEdited.current = true;

    if (parsed != null) {
      setForm((f) => {
        const mode = normalizeFundingType(f?.fundingType || "gross");
        const next = { ...f, targetGross: parsed };

        if (num(f?.investedNetProceeds) != null) {
          next.investedNetProceeds = null;
        }

        if (mode === "gross") next.targetValue = parsed;
        return next;
      });
      setGrossInputValue(formatNumber(parsed));
    } else {
      setGrossInputValue("");
      setForm((f) => {
        const mode = normalizeFundingType(f?.fundingType || "gross");
        const next = { ...f, targetGross: null, investedNetProceeds: null };
        if (mode === "gross") next.targetValue = null;
        return next;
      });
    }
  };

  const commitAmortizationPeriods = () => {
    const parsed = parseInteger(amortizationPeriodsInput);

    if (!(loanTermPeriods > 0)) {
      setAmortizationPeriodsInput("");
      setForm((f) => ({
        ...f,
        loanTermPeriods: null,
        amortizationPeriods: null,
      }));
      return;
    }

    const next = parsed != null && parsed > 0 ? parsed : loanTermPeriods;
    const bounded = Math.max(next, loanTermPeriods);

    setForm((f) => ({
      ...f,
      loanTermPeriods,
      amortizationPeriods: bounded,
    }));

    setAmortizationPeriodsInput(formatInteger(bounded));
  };

  const handleSaveWrapCashflows = (rows) => {
    const nextRows = Array.isArray(rows) ? rows : [];

    setForm((f) => ({
      ...f,
      wrapManualCashflows: nextRows,
      wrapConfig: {
        ...(normalizeWrapConfig(f?.wrapConfig) || {}),
        enabled: nextRows.length > 0,
        sourceMode: "manual",
        inputMode: "dated_totals",
        wrapPaymentRows: nextRows,
      },
    }));
  };

  const handleGenericInputChange = (value) => {
    userEdited.current = true;
    setInputValue(value);
  };

  const handleGenericInputBlur = () => commitGenericTargetValue();

  const handleGenericInputKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitGenericTargetValue();
      e.target.blur();
    }
  };

  const handleGrossInputChange = (value) => {
    userEdited.current = true;
    setGrossInputValue(value);
  };

  const handleGrossInputBlur = () => commitTargetGross();

  const handleGrossInputKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitTargetGross();
      e.target.blur();
    }
  };

  const handleAmortizationPeriodsChange = (value) => {
    setAmortizationPeriodsInput(value);
  };

  const handleAmortizationPeriodsBlur = () => commitAmortizationPeriods();

  const handleAmortizationPeriodsKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commitAmortizationPeriods();
      e.target.blur();
    }
  };

  const FundingTypeToggle = () => (
    <div
      className="inline-flex h-8 items-center rounded-md border border-border overflow-hidden"
      role="group"
      aria-label="Net Proceeds Funding Type"
    >
      {["gross", "net"].map((mode) => {
        const active = fundingType === mode;
        return (
          <button
            key={mode}
            type="button"
            onClick={() => setForm((f) => ({ ...f, fundingType: mode }))}
            aria-pressed={active}
            className={
              "h-8 px-3 text-sm font-medium transition-colors " +
              (active
                ? "btn btn-primary rounded-none"
                : "bg-app text-muted hover:text-app hover:bg-app/10")
            }
            title={
              mode === "gross"
                ? "Use Gross Net Proceeds"
                : "Use Net (After Investment)"
            }
          >
            {mode === "gross" ? "Gross" : "Net"}
          </button>
        );
      })}
    </div>
  );

  const netFundingTargetDisplay =
    !grossMismatch && investedNetProceeds != null && investedNetProceeds > 0
      ? formatNumber(investedNetProceeds)
      : "";

  const canSolveNet =
    isNetProceeds &&
    fundingType === "net" &&
    !!selectedOrg?.id &&
    !!form?.deliveryDate &&
    (num(form?.targetGross) || 0) > 0;

  const solvePrincipalEnabled =
    !isLocked && !!canSolvePrincipal && typeof onSolvePrincipal === "function";

  const amortizationPeriodsNumeric = parseInteger(
    form?.amortizationPeriods ?? amortizationPeriodsInput
  );

  const isBalloonLoan =
    isLoan &&
    loanTermPeriods > 0 &&
    amortizationPeriodsNumeric > loanTermPeriods;

  const handleSolvePrincipal = async () => {
    if (isLocked) return;

    const tv = num(form?.targetValue);

    if (tv == null || tv <= 0) {
      await showDialog({
        title: "Missing target",
        message: "Please enter a target amount before solving.",
        buttons: [{ label: "OK", value: "ok", variant: "primary" }],
        defaultAction: "ok",
      });
      return;
    }

    if (hasSolvedStructure) {
      const existingPrincipalRows = structureRows.filter(
        (row) => num(row?.principal) != null && num(row?.principal) > 0
      );
      const existingPrincipalTotal = existingPrincipalRows.reduce(
        (sum, row) => sum + num(row.principal),
        0
      );
      const resp = await showDialog({
        title: "Replace principal schedule?",
        message: `This will replace ${existingPrincipalRows.length} existing principal amount${
          existingPrincipalRows.length === 1 ? "" : "s"
        } totaling ${formatNumber(existingPrincipalTotal)}.\n\nDo you want to continue?`,
        buttons: [
          { label: "Cancel", value: "cancel", variant: "ghost" },
          { label: "OK", value: "solve", variant: "primary" },
        ],
        defaultAction: "cancel",
      });

      if (resp !== "solve") return;
    }

    try {
      await onSolvePrincipal();
    } catch (error) {
      await showDialog({
        title: "Refunding solution failed",
        message: error?.message || "Unable to solve the debt structure.",
        buttons: [{ label: "OK", value: "ok", variant: "primary" }],
        defaultAction: "ok",
      });
    }
  };

  const handleSolveNet = async () => {
    const parsedGross = parseNumber(grossInputValue);
    const gross = isRefundingDeal
      ? num(form?.targetGross)
      : parsedGross != null
      ? parsedGross
      : num(form?.targetGross);

    if (!Number.isFinite(gross) || gross <= 0) {
      await showDialog({
        title: "Solve Net",
        message: isRefundingDeal
          ? "Please enter refunded issue deposits."
          : "Please enter a valid Target Amount (Gross).",
        buttons: [{ label: "OK", value: "ok", variant: "primary" }],
        defaultAction: "ok",
      });
      return;
    }

    if (!form?.deliveryDate) {
      await showDialog({
        title: "Solve Net",
        message: "Please enter a Delivery Date in Deal Info.",
        buttons: [{ label: "OK", value: "ok", variant: "primary" }],
        defaultAction: "ok",
      });
      return;
    }

    if (!selectedOrg?.id) {
      await showDialog({
        title: "Solve Net",
        message: "Please select an Organization first.",
        buttons: [{ label: "OK", value: "ok", variant: "primary" }],
        defaultAction: "ok",
      });
      return;
    }

    if (
      !isRefundingDeal &&
      parsedGross != null &&
      parsedGross !== num(form?.targetGross)
    ) {
      setForm((f) => ({
        ...f,
        targetGross: parsedGross,
        investedNetProceeds: null,
      }));
      setGrossInputValue(formatNumber(parsedGross));
    }

    let dealId2 = form?.id ?? null;

    if (!dealId2) {
      const resp = await showDialog({
        title: "Save Issue Required",
        message:
          "To link FundPlanner to this Issue, please save the Issue first. Do you want to save now and continue?",
        buttons: [
          { label: "Cancel", value: "cancel", variant: "ghost" },
          { label: "Save and Continue", value: "save", variant: "primary" },
        ],
        defaultAction: "save",
      });

      if (resp !== "save") return;

      const ok = ensureStructure?.();
      if (!ok) return;

      const res = await saveDeal?.();
      if (!res?.ok || !res?.id) {
        return;
      }

      dealId2 = res.id;
    }

    const orgId2 = selectedOrg?.id ?? null;

    try {
      const { data: match, error } = await supabase
        .from("fp_funds")
        .select("id")
        .eq("org_id", orgId2)
        .eq("linked_ds_deal_id", dealId2)
        .eq("fund_type", linkedFundType)
        .maybeSingle();

      if (error) throw error;

      if (match?.id) {
        navigate("/fund-planner/structurer", {
          state: {
            dsToFundPlanner: {
              kind: "open",
              fundId: match.id,
              linkedDealId: dealId2,
              totalAmount: Number(gross),
              fundType: linkedFundType,
            },
          },
        });
        return;
      }

      navigate("/fund-planner/structurer", {
        state: {
          dsToFundPlanner: {
            kind: "handoff",
            linkedDealId: dealId2,
            fundType: linkedFundType,
            settlementDate: form.deliveryDate,
            totalAmount: Number(gross),
          },
        },
      });
    } catch (e) {
      console.error("❌ [Solve Net] fund lookup failed:", e);
      await showDialog({
        title: "Solve Net",
        message: e?.message || "Failed to launch FP Structurer.",
        buttons: [{ label: "OK", value: "ok", variant: "primary" }],
        defaultAction: "ok",
      });
    }
  };

  const targetTypeLabel = isRefundingDeal ? "Refunding" : "Net Proceeds";
  const grossTargetLabel = isRefundingDeal
    ? "Refunding Requirement (Gross)"
    : isCombinationDeal
    ? "New Money Requirement"
    : "Target Amount";

  if (isRefundingDeal) {
    return (
      <div className="flex flex-col gap-4 mb-3">
        <div className="flex items-end gap-4 flex-wrap">
          <DropdownSelect
            label="Refunding Solution"
            ariaLabel="Refunding Solution"
            value={enabledRefundingSolutionType(form?.refundingSolutionType)}
            onChange={(value) => setForm((f) => ({ ...f, refundingSolutionType: value }))}
            className="min-w-[300px]"
            options={REFUNDING_SOLUTION_OPTIONS}
          />
          <div className="flex flex-col">
            <label className="text-xs font-medium text-muted mb-1">Refunding Requirement</label>
            <DisplayField title="Derived from the selected refunded maturities">
              {refundingDepositsTotal != null ? formatNumber(refundingDepositsTotal) : "—"}
            </DisplayField>
          </div>
        </div>
        {!isLocked && (
          <div>
            <button type="button" className="btn btn-primary" onClick={handleSolvePrincipal} disabled={!solvePrincipalEnabled}>
              Solve Refunding
            </button>
          </div>
        )}
      </div>
    );
  }

return (
  <>
    <div className="flex flex-col gap-3 mb-3">
      {isCombinationDeal && (
        <div className="flex items-end gap-4 flex-wrap">
          <div className="flex flex-col">
            <label className="text-xs font-medium text-muted mb-1">Refunding Requirement</label>
            <DisplayField className="text-muted">
              {formatNumber(combinationRequirements.refundingRequirement)}
            </DisplayField>
          </div>
          {targetType !== "par_amount" && (
            <div className="flex flex-col">
              <label className="text-xs font-medium text-muted mb-1">Total Requirement</label>
              <DisplayField className="text-muted">
                {formatNumber(combinationRequirements.totalRequirement)}
              </DisplayField>
            </div>
          )}
        </div>
      )}
      <div className="flex items-end gap-3 flex-wrap">
        <DropdownSelect
          label="Schedule Method"
          ariaLabel="Schedule Method"
          value={structureMode}
          onChange={(value) =>
            setForm((f) => ({ ...f, structureMode: value }))
          }
          className="min-w-[240px]"
          placeholder=""
          options={[
            { value: "level_payment", label: "Level Debt Service" },
            ...(isLoan
              ? [{ value: "level_principal", label: "Level Principal" }]
              : []),
            { value: "wrap_lds", label: "Wrap Debt Service" },
          ]}
        />

        {isLoan && loanTermPeriods > 0 && (
          <div className="flex flex-col min-w-[240px]">
            <label className="text-xs font-medium text-muted mb-1">
              Loan Amortization
            </label>

            <DisplayField
              className="text-muted"
              title={
                isBalloonLoan
                  ? "Amortization period count exceeds the actual loan term, creating a balloon at final maturity."
                  : "Amortization period count equals the actual loan term."
              }
            >
              {isBalloonLoan
                ? `${loanTermPeriods}/${amortizationPeriodsNumeric} balloon`
                : `${loanTermPeriods}/${loanTermPeriods} fully amortizing`}
            </DisplayField>
          </div>
        )}

        {isLoan && (
          <TextInput
            label={`Amortization Periods (${
              form?.principalFrequency
                ? form.principalFrequency.charAt(0).toUpperCase() +
                  form.principalFrequency.slice(1)
                : "Annual"
            })`}
            id="loan-amortization-periods-input"
            type="text"
            inputMode="numeric"
            value={amortizationPeriodsInput}
            onChange={handleAmortizationPeriodsChange}
            onBlur={handleAmortizationPeriodsBlur}
            onKeyDown={handleAmortizationPeriodsKeyDown}
            placeholder={
              loanTermPeriods > 0 ? String(loanTermPeriods) : "e.g. 120"
            }
            className="w-[100px]"
            title={
              loanTermPeriods > 0
                ? `Loan term is ${loanTermPeriods} periods. Increase this value to create a balloon structure.`
                : "Enter the amortization period count for the loan."
            }
          />
        )}

        {isWrapMode && (
          <div className="flex flex-col min-w-[320px]">
            <label className="text-xs font-medium text-muted mb-1">
              Existing Debt Service
            </label>

            <div className="flex items-center gap-3 flex-wrap">
              <DisplayField title="Raw dated total amounts to be wrapped by the new issue">
                {wrapStats.count > 0
                  ? `${wrapStats.count} rows, ${formatNumber(
                      wrapStats.totalAmount
                    )} total`
                  : "No wrap cash flows entered"}
              </DisplayField>

              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setWrapModalOpen(true)}
              >
                {wrapStats.count > 0 ? "Enter/Edit" : "Enter Cash Flows"}
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="flex items-end gap-3 flex-wrap">
        {isRefundingDeal ? (
          <DropdownSelect
            label="Target Type"
            ariaLabel="Target type"
            value="net_proceeds"
            disabled
            title="Refunding deals currently support only Refunding target type"
            className="min-w-[180px] opacity-100"
            placeholder=""
            options={[{ value: "net_proceeds", label: targetTypeLabel }]}
          />
        ) : (
          <DropdownSelect
            label="Target Type"
            ariaLabel="Target type"
            value={targetType}
            className="min-w-[180px]"
            placeholder=""
            options={[
              {
                value: "par_amount",
                label:
                  String(form?.financingType || "").toLowerCase() === "loan"
                    ? "Principal Amount"
                    : "Par Amount",
              },
              { value: "net_proceeds", label: "Net Proceeds" },
              {
                value: "periodic_ds",
                label: "Periodic Debt Service",
                disabled: structureMode === "wrap_lds",
              },
            ]}
            onChange={(nextType) => {
              userEdited.current = false;

              setForm((f) => {
                const prevType = f?.targetType;
                const cache = { ...(f?.targetValueCache || {}) };

                if (prevType && prevType !== "net_proceeds") {
                  cache[prevType] = num(f?.targetValue);
                }

                if (nextType === "periodic_ds") {
                  const restored = cache.periodic_ds ?? null;
                  seededParFromDealInfo.current = true;

                  return {
                    ...f,
                    targetType: "periodic_ds",
                    targetValue: restored,
                    targetValueCache: cache,
                  };
                }

                if (nextType === "net_proceeds") {
                  const mode = normalizeFundingType(f?.fundingType || "gross");
                  const g = num(f?.targetGross);
                  const inv = num(f?.investedNetProceeds);

                  const next = {
                    ...f,
                    targetType: "net_proceeds",
                    fundingType: mode,
                    targetGross: g,
                    targetValueCache: cache,
                  };

                  next.targetValue =
                    mode === "gross"
                      ? g != null && g > 0
                        ? g
                        : null
                      : inv != null && inv > 0
                      ? inv
                      : null;

                  return next;
                }

                if (nextType === "par_amount") {
                  const restored = cache.par_amount;
                  const p = num(f?.parAmount);

                  const nextTv =
                    restored != null
                      ? restored
                      : p != null && p > 0
                      ? p
                      : null;

                  if (!hasSolvedStructure) {
                    seededParFromDealInfo.current = false;
                  }

                  return {
                    ...f,
                    targetType: "par_amount",
                    targetValue: nextTv,
                    targetValueCache: {
                      ...cache,
                      par_amount: nextTv,
                    },
                  };
                }

                const restored = cache[nextType] ?? null;

                return {
                  ...f,
                  targetType: nextType,
                  targetValue: restored,
                  targetValueCache: cache,
                };
              });
            }}
          />
        )}

        {isNetProceeds ? (
          <>
            <div className="flex flex-col">
              {isRefundingDeal ? (
                <>
                  <label className="text-xs font-medium text-muted mb-1">
                    {grossTargetLabel}
                  </label>

                  <DisplayField
                    ariaLabel="Refunding Deposits Required"
                    title="Derived from Refunded Issues deposits"
                  >
                    {refundingDepositsTotal != null
                      ? formatNumber(refundingDepositsTotal)
                      : "-"}
                  </DisplayField>
                </>
              ) : (
                <TextInput
                  label={grossTargetLabel}
                  type="text"
                  inputMode="decimal"
                  value={grossInputValue}
                  onChange={handleGrossInputChange}
                  onBlur={handleGrossInputBlur}
                  onKeyDown={handleGrossInputKeyDown}
                  placeholder="e.g. 100,000,000"
                  className="min-w-[220px]"
                />
              )}
            </div>

            {String(form?.financingType || "").toLowerCase() !== "loan" && (
              <div className="flex flex-col">
                <label className="text-xs font-medium text-muted mb-1">
                  Funding Type
                </label>
                <FundingTypeToggle />
              </div>
            )}

            {fundingType === "net" && (
              <div className="flex flex-col min-w-[320px]">
                <label className="text-xs font-medium text-muted mb-1">
                  Required Deposit (Net)
                </label>

                <div className="flex items-center gap-3 flex-wrap">
                  <DisplayField
                    ariaLabel="Net Funding Target"
                    title={
                      !linkedFundId
                        ? "No linked fund yet"
                        : grossMismatch
                        ? "Target gross does not match linked fund total amount. Update in FP Structurer, then re-solve."
                        : `Derived from Total Portfolio Cost in the linked ${
                            linkedFundType === "refunding"
                              ? "Refunding"
                              : "Project"
                          } fund`
                    }
                  >
                    {linkedFundLoading
                      ? "Loading…"
                      : netFundingTargetDisplay || "-"}
                  </DisplayField>

                  <button
                    type="button"
                    className="btn btn-primary"
                    disabled={!canSolveNet}
                    onClick={handleSolveNet}
                    title={
                      !selectedOrg?.id
                        ? "Select an Organization"
                        : !form?.deliveryDate
                        ? "Enter a Delivery Date"
                        : !form?.id
                        ? "Will prompt you to save the Issue to create an ID"
                        : "Launch FP Structurer"
                    }
                  >
                    Launch FP Structurer
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <TextInput
            label={
              isCombinationDeal && targetType === "par_amount"
                ? String(form?.financingType || "").toLowerCase() === "loan"
                  ? "Principal Amount"
                  : "Total Principal Amount"
                : isCombinationDeal
                ? "New Money Requirement"
                : "Target Amount"
            }
            id="target-value-input"
            type="text"
            inputMode="decimal"
            value={inputValue}
            onChange={handleGenericInputChange}
            onBlur={handleGenericInputBlur}
            onKeyDown={handleGenericInputKeyDown}
            placeholder="e.g. 100,000,000"
            className="min-w-[220px]"
          />
        )}
      </div>

      {!isLocked && (
        <div className="flex justify-start">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSolvePrincipal}
            disabled={!solvePrincipalEnabled}
            title={
              !solvePrincipalEnabled
                ? "Enter a target amount and ensure structure exists"
                : undefined
            }
          >
            Solve
          </button>
        </div>
      )}
    </div>

    <WrapCashflowModal
      open={wrapModalOpen}
      initialRows={
        Array.isArray(form?.wrapManualCashflows) &&
        form.wrapManualCashflows.length > 0
          ? form.wrapManualCashflows
          : wrapConfig.wrapPaymentRows
      }
      onClose={() => setWrapModalOpen(false)}
      onSave={handleSaveWrapCashflows}
    />
  </>
);
}
